/**
 * Offline attendance queue (PWA).
 * When the user checks in a student/therapist while offline, the request is queued
 * in localStorage and drained on next `online` event or app load.
 */
import api from "@/lib/api";
import { toast } from "sonner";

const KEY = "tf_offline_queue";

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
  catch { return []; }
}
function write(list) {
  localStorage.setItem(KEY, JSON.stringify(list));
}

export function isOnline() {
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}

export function queueSize() {
  return read().length;
}

/** Enqueue a check-in — returns true if the request was queued (offline). */
export function enqueueCheckIn(payload) {
  const list = read();
  list.push({
    id: `off_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    endpoint: "/attendance/check-in",
    method: "POST",
    body: payload,
    at: new Date().toISOString(),
  });
  write(list);
}

/** Try to drain the queue. Called on app load and on 'online' event. */
export async function drainQueue() {
  if (!isOnline()) return { drained: 0, remaining: queueSize() };
  const list = read();
  if (list.length === 0) return { drained: 0, remaining: 0 };
  const remaining = [];
  let drained = 0;
  for (const item of list) {
    try {
      await api.request({ url: item.endpoint, method: item.method, data: item.body });
      drained += 1;
    } catch (e) {
      // If it's a 400 "already checked in", drop it as a stale duplicate.
      const status = e?.response?.status;
      if (status === 400) { drained += 1; continue; }
      remaining.push(item);
    }
  }
  write(remaining);
  return { drained, remaining: remaining.length };
}

/** Register listeners. Call once on app mount. */
export function installOfflineSync() {
  if (typeof window === "undefined") return;
  window.addEventListener("online", async () => {
    const { drained, remaining } = await drainQueue();
    if (drained > 0) {
      toast.success(`Synced ${drained} offline check-in${drained === 1 ? "" : "s"}${remaining ? ` (${remaining} pending)` : ""}`);
    }
  });
  // Initial drain
  drainQueue().then(({ drained }) => {
    if (drained > 0) toast.success(`Synced ${drained} pending check-in${drained === 1 ? "" : "s"}`);
  });
}
